import _ from 'lodash';
import React, { useContext, useMemo } from 'react';
import { generateFieldId, getAccountActivityRuleErrors, shouldShowField, validateField as validateJsonField } from '../../../../../components/InputField/helper';
import { WrapperField } from '../../../../../components/InputField/WrapperField';
import { DialogContext } from '../../../../../store/context/DialogContext';
import { useTranslation } from '../../../../../store/context/TranslationContext';
import ButtonGroupNew from '../../BusinessLiteForms/components/ButtonGroupNew';
import { getFirstOrderedMappedErrorMessage } from '../../helpers/validationOrder';
import { TRANSACTION_FIELDS } from '../../IPlushelper';
import { CONVERSIONS_FIELD_MAP } from '../../IndividualPlus/constant';
import { QuestionRadioGroup } from './DeclarationForm';
import { validateRequiredFields } from './helper';
import { buildAutomationChildTestId, normalizeAutomationTestIdSegment } from '../../../../../components/InputField/testId';
import { VARIANT, variantClass } from '../../../../../util/variant';
import DocumentFirstStepHead from '../../BusinessV2/components/DocumentFirstStepHead';
import BusinessV2FieldGrid from '../../BusinessV2/BusinessV2FieldGrid';
import '../../BusinessV2/documentsVersion2.css';

const EXPECTED_ACTIVITY_TEST_ID = "individual-plus-expected-account-activity";

// Doc-first (GMA) "Volume" step: self-declared volume tiers + amounts. Order comes from
// wizardContent.AccountActivity.uiSections when the config carries it; this is the fallback.
const FALLBACK_VOLUME_FIELDS = [
    'expectedDepositVolumeTier',
    'expectedDepositVolumeTierOther',
    'expectedPaymentVolumeTier',
    'expectedPaymentVolumeTierOther',
    'Amount_Of_Initial_Deposit',
    'Average_Balance',
];

const buildExpectedActivityFieldTestId = (field, suffix) => (
    buildAutomationChildTestId(
        EXPECTED_ACTIVITY_TEST_ID,
        `${suffix}-${normalizeAutomationTestIdSegment(field)}`
    )
);

const ExpectedAccountActivityForm = ({
    hideButtons,
    asLabel,
    data,
    handleChange,
    handleChangeObject,
    handleUpdate,
    hasError,
    setHasError,
    setErrorMessage = () => { },
    loading,
    setStep,
    cyclosFields,
    jsonConfig,
    // Doc-first GMA (formVersion >= 2 broker) shows volume tiers; legacy (existing onboarding +
    // older GMA broker) keeps the original conversion questions + ACH/Wire activity matrix.
    isDocumentFirst = false,
    // When the Review/Documents step is skipped (no FINAL-phase docs), this step is the broker's
    // last step and its Next submits the application instead of advancing.
    isBrokerSubmitStep = false,
    brokerSubmitNextStep,
    backStep = 2,
    nextStep = 4,
    pageNum,
    stepCount = 6,
    stepLabel = 'Expected Account Activity',
}) => {
    const { showError, showConfirm } = useContext(DialogContext);
    const { t } = useTranslation();

    // --- Doc-first volume fields ---
    const volumeFields = useMemo(() => {
        const uiSections = jsonConfig?.wizardContent?.AccountActivity?.uiSections;
        const configured = Array.isArray(uiSections)
            ? uiSections.flatMap((section) => (Array.isArray(section?.fields) ? section.fields : []))
            : null;
        return (configured && configured.length) ? configured : FALLBACK_VOLUME_FIELDS;
    }, [jsonConfig]);

    // --- Legacy activity-matrix fields ---
    const numericFields = useMemo(
        () => (["Amount_Of_Initial_Deposit", ...(TRANSACTION_FIELDS ?? [])]),
        []
    );

    const conversionPlanFields = useMemo(
        () => ([
            { field: 'hasCoinConversionPlan', question: 'Do you plan on converting stablecoins to/from fiat in FV Bank? (USDC, USDT, PYUSD or similar)' },
            { field: 'hasCryptoConversionPlan', question: 'Do you plan on converting cryptocurrencies to/from fiat in FV Bank? (BTC, ETH, or similar)' },
        ]),
        []
    );

    const askForConversionValueUpdate = async (payloadToCheck) => {
        const fieldsToUpdate = [];
        if (
            payloadToCheck?.hasCoinConversionPlan === false && (
                payloadToCheck?.Number_Of_Monthly_Stablecoin_Conversions_Deposits > 0 ||
                payloadToCheck?.Amount_Of_Monthly_Stablecoin_Conversions_Deposits > 0 ||
                payloadToCheck?.Number_Of_Monthly_Stablecoin_Conversions_Payments > 0 ||
                payloadToCheck?.Amount_Of_Monthly_Stablecoin_Conversions_Payments > 0
            )) {
            fieldsToUpdate.push("hasCoinConversionPlan");
        }
        if (
            payloadToCheck?.hasCryptoConversionPlan === false && (
                payloadToCheck?.Count_Of_Monthly_Cryptocurrency_Conversions > 0 || payloadToCheck?.Amount_Of_Monthly_Cryptocurrency_Conversions > 0
            )) {
            fieldsToUpdate.push("hasCryptoConversionPlan");
        }
        if (fieldsToUpdate.length === 0) {
            return { success: true, fieldsToUpdate: [], error: "" };
        } else {
            const title = fieldsToUpdate.map(field => CONVERSIONS_FIELD_MAP?.[field] ?? field).join(" and ");
            const response = await showConfirm({
                title: t(`Do you want to update ${title} to Yes?`),
            });
            if (response) return { success: true, fieldsToUpdate, error: '' };
            else return { success: false, fieldsToUpdate, error: `You have to update ${title} to Yes or change their values to 0 to proceed.` };
        }
    }

    const handleSubmit = async () => {
        if (isDocumentFirst) {
            const volumeErrors = volumeFields
                .map((field) => {
                    const jsonField = cyclosFields?.[field];
                    if (!jsonField) return null;
                    if (!shouldShowField(data, jsonField?.showIf)) return null;
                    const value = _.get(data, field);
                    const error = validateJsonField(jsonField, value, data);
                    return error ? { field, message: error } : null;
                })
                .filter(Boolean);

            const visibleFieldOrder = volumeFields.filter((field) => {
                const jsonField = cyclosFields?.[field];
                return jsonField && shouldShowField(data, jsonField?.showIf);
            });

            const mappedErrors = {};
            volumeErrors.forEach(({ field, message }) => _.set(mappedErrors, field, message));

            const firstErrorMessage = getFirstOrderedMappedErrorMessage(visibleFieldOrder, mappedErrors);
            if (firstErrorMessage) {
                setHasError(true);
                setErrorMessage(firstErrorMessage);
                showError(firstErrorMessage);
                return;
            }

            setHasError(false);
            if (isBrokerSubmitStep) {
                // Review step is skipped (no FINAL docs): this is the broker's last step, so submit.
                return handleUpdate({ isfetchDetail: true, isLastStep: true, skipUpdate: true, nextStep: brokerSubmitNextStep });
            }
            return handleUpdate({ nextStep });
        }

        const planErrorEntries = conversionPlanFields
            .map(({ field }) => {
                const message = validateRequiredFields([{ field, fieldType: 'checkbox' }], data, cyclosFields)?.[0];
                return message ? { field, message } : null;
            })
            .filter(Boolean);

        const expectedActivityErrors = numericFields
            .map((field) => {
                const jsonField = cyclosFields?.[field];
                if (!jsonField) return null;
                if (!shouldShowField(data, jsonField?.showIf)) return null;
                const value = _.get(data, field);
                const error = validateJsonField(jsonField, value, data);
                return error ? { field, message: error } : null;
            })
            .filter(Boolean);

        const crossFieldErrors = getAccountActivityRuleErrors(data, {
            depositLocationFields: ["depositLocations"],
            paymentLocationFields: ["paymentLocations"]
        });

        const visibleFieldOrder = [
            ...conversionPlanFields.map(({ field }) => field),
            ...numericFields.filter((field) => {
                const jsonField = cyclosFields?.[field];
                return jsonField && shouldShowField(data, jsonField?.showIf);
            }),
        ];

        const mappedErrors = {};
        [...planErrorEntries, ...expectedActivityErrors].forEach(({ field, message }) => {
            _.set(mappedErrors, field, message);
        });
        crossFieldErrors.forEach((err) => {
            const fields = Array.isArray(err.activityFields) && err.activityFields.length
                ? [...err.activityFields, err.field]
                : [err.field];
            fields.filter(Boolean).forEach((field) => _.set(mappedErrors, field, err.message));
        });

        const firstErrorMessage = getFirstOrderedMappedErrorMessage(visibleFieldOrder, mappedErrors);
        if (firstErrorMessage) {
            setHasError(true);
            setErrorMessage(firstErrorMessage);
            showError(firstErrorMessage);
            return;
        }

        const { success, fieldsToUpdate, error } = await askForConversionValueUpdate(data);
        if (!success) {
            setHasError(true);
            setErrorMessage(error);
            showError(error);
            return;
        }

        const payloadToSubmit = fieldsToUpdate?.length
            ? { ...data, ...Object.fromEntries(fieldsToUpdate.map(field => [field, true])) }
            : data;

        setHasError(false);
        return handleUpdate({ nextStep, payload: payloadToSubmit });
    };

    const volumeFieldOrder = volumeFields.filter((field) => cyclosFields?.[field]);

    const hasAnyNumericField = numericFields.some((field) => cyclosFields?.[field]);

    const activityFooter = (actionsClassName) => (
        !hideButtons ? (
            <div className={actionsClassName} data-testid={buildAutomationChildTestId(EXPECTED_ACTIVITY_TEST_ID, "footer")}>
                <ButtonGroupNew
                    onNextClick={handleSubmit}
                    onBackClick={() => { setStep(backStep) }}
                    step={3}
                    showPageNum={false}
                    pageNum={3}
                    isDisabled={asLabel}
                    asLabel={false}
                    loading={loading}
                    testId={buildAutomationChildTestId(EXPECTED_ACTIVITY_TEST_ID, "navigation")}
                />
            </div>
        ) : null
    );

    // Doc-first (GMA broker, formVersion >= 2) renders the V2 shell + single volume grid.
    if (isDocumentFirst) {
        return (
            <div className={`business-v2-step-shell docfirst ${variantClass(VARIANT.V2)}`.trim()} data-testid={EXPECTED_ACTIVITY_TEST_ID}>
                <DocumentFirstStepHead
                    stepIndex={pageNum}
                    stepCount={stepCount}
                    stepLabel={stepLabel}
                    title={t(stepLabel)}
                    intro={t('Tell us the volume you expect on the account.')}
                    dataTestId={buildAutomationChildTestId(EXPECTED_ACTIVITY_TEST_ID, 'head')}
                />
                <BusinessV2FieldGrid
                    variant={VARIANT.V2}
                    fieldOrder={volumeFieldOrder}
                    fieldMap={cyclosFields}
                    data={data}
                    asLabel={asLabel}
                    handleChange={handleChangeObject}
                    hasError={hasError}
                    dataTestId={buildAutomationChildTestId(EXPECTED_ACTIVITY_TEST_ID, "volume-section")}
                />
                {activityFooter('business-v2-step-actions')}
            </div>
        );
    }

    // Legacy individual flow (existing onboarding + older GMA brokers): the pre-15920 markup,
    // so normal onboarding is untouched.
    return (
        <div className="mt-4" data-testid={EXPECTED_ACTIVITY_TEST_ID}>
            <h3 className="fw-bold" data-testid={buildAutomationChildTestId(EXPECTED_ACTIVITY_TEST_ID, "title")}>{t("Expected Account Activity")}</h3>

            <div className="mt-3" data-testid={buildAutomationChildTestId(EXPECTED_ACTIVITY_TEST_ID, "conversion-plan-section")}>
                {conversionPlanFields.map(({ field, question }) => (
                    cyclosFields?.[field]?.uiType ? (
                        <WrapperField
                            key={field}
                            disabled={asLabel}
                            jsonField={cyclosFields?.[field]}
                            dataObj={data}
                            onChange={handleChangeObject}
                            showError={hasError}
                            onError={() => { }}
                        />
                    ) : (
                        <QuestionRadioGroup
                            key={field}
                            id={cyclosFields?.[field]?.wizard ? generateFieldId(cyclosFields?.[field]) : buildExpectedActivityFieldTestId(field, "field")}
                            required
                            field={field}
                            question={cyclosFields?.[field]?.label ?? question}
                            data={data}
                            handleChange={handleChange}
                            hasError={hasError}
                            isDisabled={asLabel}
                            error={validateRequiredFields([{ field, fieldType: 'checkbox' }], data, cyclosFields)[0]}
                        />
                    )
                ))}
            </div>

            {hasAnyNumericField ? (
                <div className="transaction-table p-0 mb-3" data-testid={buildAutomationChildTestId(EXPECTED_ACTIVITY_TEST_ID, "activity-table-section")}>
                    <table className="transaction-value-table" data-testid={buildAutomationChildTestId(EXPECTED_ACTIVITY_TEST_ID, "activity-table")}>
                        <thead data-testid={buildAutomationChildTestId(EXPECTED_ACTIVITY_TEST_ID, "activity-table-head")}>
                            <tr>
                                <th className="header-cell" data-testid={buildAutomationChildTestId(EXPECTED_ACTIVITY_TEST_ID, "activity-table-header-question")}>{t("Question")}</th>
                                <th className="header-cell text-end value-cell" data-testid={buildAutomationChildTestId(EXPECTED_ACTIVITY_TEST_ID, "activity-table-header-value")}>{t("Expected Activity")}</th>
                            </tr>
                        </thead>
                        <tbody data-testid={buildAutomationChildTestId(EXPECTED_ACTIVITY_TEST_ID, "activity-table-body")}>
                            {numericFields.map((field) => {
                                const jsonField = cyclosFields?.[field];
                                if (!jsonField) return null;

                                const resolvedJsonField = field === "Amount_Of_Initial_Deposit"
                                    ? {
                                        ...jsonField,
                                        uiType: "numberField",
                                        type: jsonField?.type ?? "number",
                                        placeholder: jsonField?.placeholder ?? "Amount",
                                    }
                                    : jsonField;

                                const isFieldVisible = shouldShowField(data, resolvedJsonField?.showIf);
                                const fieldKey = normalizeAutomationTestIdSegment(field);
                                return (
                                    <tr
                                        key={field}
                                        style={isFieldVisible ? undefined : { display: 'none' }}
                                        data-testid={buildAutomationChildTestId(EXPECTED_ACTIVITY_TEST_ID, `activity-row-${fieldKey}`)}
                                    >
                                        <td data-testid={buildAutomationChildTestId(EXPECTED_ACTIVITY_TEST_ID, `activity-cell-question-${fieldKey}`)}>{t(resolvedJsonField?.label)}</td>
                                        <td className="p-0 m-0 transaction-input text-end value-cell" data-testid={buildAutomationChildTestId(EXPECTED_ACTIVITY_TEST_ID, `activity-cell-value-${fieldKey}`)}>
                                            <WrapperField
                                                disabled={asLabel}
                                                jsonField={resolvedJsonField}
                                                dataObj={data}
                                                onChange={handleChangeObject}
                                                showError={hasError}
                                                onError={() => { }}
                                                customProp="plain"
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : null}
            {activityFooter('mt-4')}
        </div>
    );
};

export default ExpectedAccountActivityForm;